import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Positions a floating panel against the element that opened it.
//
// Positioned with `fixed`, from the trigger's measured rectangle: relative to
// the viewport, it escapes any ancestor's overflow — a table wrapper with
// `overflow-x: auto` is a scroll container and would clip an absolutely
// positioned row menu — and a panel opens next to what was clicked.
//
// The panel is measured after mount so it can be flipped or clamped by its real
// size rather than a guess: useLayoutEffect runs before paint, so a panel that
// needs flipping never appears in the wrong place first.
//
// An `anchor` handed in (#580) goes straight to `place`, and changing it places
// again. The event popover is not wrapped around the lesson that opens it, so
// it is handed the clicked card; copying it into `triggerRef` from a later
// layout effect would leave the first placement with no trigger and the panel
// hidden until a scroll or a resize.

const MARGIN = 8;

// Where the panel goes, from the trigger's viewport rectangle, the panel's
// size and the viewport's. Pure, so it is tested without a layout engine
// (test/ui/anchoredPanel.test.jsx).
export function anchoredPosition({
  trigger: t, panel: { width: pw, height: ph }, viewport: { width: vw, height: vh },
  align = 'right', gap = 4,
}) {
  let left = align === 'left' ? t.left : t.right - pw;
  // Clamp rather than flip horizontally: a menu that jumps to the other side
  // of its button is harder to follow than one that shifts to stay on screen.
  left = Math.max(MARGIN, Math.min(left, vw - pw - MARGIN));

  // Flip vertically, though — below is the default, and a panel that would run
  // off the bottom belongs above its trigger rather than jammed against the
  // edge with its content unreachable.
  let top = t.bottom + gap;
  if (top + ph > vh - MARGIN && t.top - gap - ph > MARGIN) top = t.top - gap - ph;
  top = Math.max(MARGIN, Math.min(top, vh - ph - MARGIN));

  return { position: 'fixed', top: Math.round(top), left: Math.round(left) };
}

export default function useAnchoredPanel({ open, align = 'right', gap = 4, anchor = null }) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [style, setStyle] = useState(null);

  const place = useCallback(() => {
    const trigger = anchor || triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    setStyle(anchoredPosition({
      trigger: trigger.getBoundingClientRect(),
      panel: { width: panel.offsetWidth, height: panel.offsetHeight },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      align,
      gap,
    }));
  }, [align, gap, anchor]);

  useLayoutEffect(() => {
    if (!open) { setStyle(null); return undefined; }
    place();
    // Anything that moves the trigger moves the panel with it. Capture phase so
    // scrolling inside a table wrapper counts, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  return { triggerRef, panelRef, style };
}
